import { Injectable } from '@angular/core';
import { FeatureActionsMap } from './../../classes/authentication/user.class';
import { AuthenticationService } from './../../services/authentication.service';
import { DiskService, OFFLINE_DATA_COUNT_ENTITY_NAME } from './../../services/disk/disk.service';
import { DB_KEY_PREFIXES } from './../../config/pouch-db.config';
import { NextCallObjectiveService } from '../../services/next-call-objectives/next.call.objectives.service';
import { DynamicsClientService } from '../dynamics-client/dynamics-client.service';
import { fetchQueries } from './../../config/dynamics-fetchQueries';
import { NextCallObjective, NextCallObjectiveServiceDTO, NextCallObjectiveStateCode, NextCallObjectiveStatusCode } from '../../classes/Next-Call-Objective/next.call.objective';
import { OFFLINE_ID_PREFIX } from '../../classes/activity/appointment.activity.class';
import { HttpClient } from '@angular/common/http';
import { Endpoints } from "../../../config/endpoints.config";
import { DeviceService } from '../../services/device/device.service';
import { find, findIndex } from 'lodash-es';


@Injectable({
  providedIn: 'root'
})
export class NextCallObjectiveDataService {

  constructor(
    private authService: AuthenticationService,
    private disk: DiskService,
    private nextCallObjectiveService: NextCallObjectiveService,
    private dynamics: DynamicsClientService,
    private http: HttpClient,
    public device: DeviceService,
  ) {

  }
  /**
   *loadOfflineNextCallObjectives
   *@param: Nonethis.nextCallObjectiveService.
   */
  async loadOfflineNextCallObjectives() {
    if (this.authService.hasFeatureAction(FeatureActionsMap.MEETING_OBJECTIVES)) {
      try {
        await this.disk.retrieve(DB_KEY_PREFIXES.NEXT_CALL_OBJECTIVES, true).then((doc) => {
          if (doc && doc.raw) {
            this.nextCallObjectiveService.nextCallObjectives = doc.raw
          }
          else {
            this.nextCallObjectiveService.nextCallObjectives = [];
          }
        });
      }
      catch (error) {
        this.nextCallObjectiveService.nextCallObjectives = [];
      }
    }
  }

/**
 * getnextCallObjectives
 * Intial And Delta Sync for Next call Objective
 * @param: fullSync: boolean
 */
  async getnextCallObjectives(fullSync?: boolean, loadFromDbOnly = false) {
    if (this.authService.hasFeatureAction(FeatureActionsMap.MEETING_OBJECTIVES)) {
      if (loadFromDbOnly) {
        this.loadOfflineNextCallObjectives();
      } else {
        try {
          let offlineDataStored;
          let lastModifiedForDeltaSync;
          await this.disk.retrieve(DB_KEY_PREFIXES.NEXT_CALL_OBJECTIVES, true).then((doc) => {
            offlineDataStored = doc
            if (doc && doc.raw) {
              this.nextCallObjectiveService.nextCallObjectives = doc.raw;
            }
            else {
              this.nextCallObjectiveService.nextCallObjectives = [];
              fullSync = true;
            }
          })

          let fetchXML = fetchQueries.nextCallObjectives;
          const now = new Date();//this will be used to find teh Difference and Save the last modified time for
          // isDelta drives aggregateNextCallObjectives' merge-vs-replace decision below — must
          // reflect whether we're actually about to send a modifiedon-filtered request, not just
          // the fullSync flag on its own (still true whenever lastModifiedForDeltaSync is unknown).
          let isDelta = false;

          // Server-side user scoping — indskr_user is a plain lookup on indskr_callobjective
          // itself, no customer/position join needed.
          fetchXML = fetchXML.replace('{currentUserId}', this.authService.user.systemUserID);

          // Same offline-data-window bound used elsewhere in the app (contact.service.ts,
          // call-plan.offline.service.ts, SDR/customer-events sync) — without it, a full sync
          // pulls every call objective the user has ever created, regardless of age.
          const createdOnLimitIso = new Date(this.authService.getPastOfflineDataLimitDateInUTCMillisecond()).toISOString();
          fetchXML = fetchXML.replace('{createdOnLimitFilter}', createdOnLimitIso);

          // Always pending-only, full or delta — this app only ever needs OPEN objectives to
          // offer for capture. Completed-objective visibility (a since-completed row disappearing
          // from here, and surfacing on whichever meeting/self-activity it was completed against)
          // is a backend concern, not handled by this fetch or its merge.
          fetchXML = fetchXML.replace('{statusFilter}',
            `<condition attribute="statuscode" operator="eq" value="${NextCallObjectiveStatusCode.pending}" />`);

          if (fullSync) {
            // Initial sync — no prior data, fetch everything.
            fetchXML = fetchXML.replace('{modifiedOnFilter}', '');
          }
          else {
            // Real modifiedon-based delta filter. This entity's Dataverse change-tracking never
            // worked (see the removed trackchange-based attempt in git history), so this fetches
            // directly off indskr_callobjective.modifiedon instead of an indskr_trackchange join —
            // lastModifiedForDeltaSync is the same timestamp already being persisted below on every
            // sync, previously computed only to feed the abandoned trackchange filter.
            lastModifiedForDeltaSync = offlineDataStored.lastModified;
            if (lastModifiedForDeltaSync) {
              isDelta = true;
              // Dataverse needs ISO 8601 for datetime conditions, not a raw epoch-ms number —
              // confirmed via a live 0x80040201 "date-time format ... invalid" failure elsewhere
              // in this app when this conversion was missed.
              const modifiedOnIso = new Date(Number(lastModifiedForDeltaSync)).toISOString();
              // Bare condition, not wrapped in its own <filter> — the template already places
              // {modifiedOnFilter} directly inside a <filter type="and">.
              fetchXML = fetchXML.replace('{modifiedOnFilter}',
                `<condition attribute="modifiedon" operator="on-or-after" value="${modifiedOnIso}" />`);
            } else {
              fetchXML = fetchXML.replace('{modifiedOnFilter}', '');
            }
          }

          await this.dynamics.executeFetchQuery('indskr_callobjectives', fetchXML)
            .then(async (res) => {
              this.aggregateNextCallObjectives(res, isDelta);

              this.disk.updateOrInsert(DB_KEY_PREFIXES.NEXT_CALL_OBJECTIVES, (doc) => {
                doc = {
                  raw: []
                };
                doc.raw =[...this.nextCallObjectiveService.nextCallObjectives];

                doc.lastModified = now.getTime();
                return doc;
              });
            },
              (err) => {
              })
        } catch (error) {
        }
      }
    }
  }

  /**
   * @param isDelta when true, incoming rows are only a partial slice (a modifiedon-filtered delta
   * fetch, itself always pending-only per {statusFilter} in dynamics-fetchQueries.ts) and must be
   * merged into the existing cached array — upsert active rows, remove rows whose stateCode is no
   * longer active — rather than replacing it wholesale. Replacing wholesale on a delta response
   * would silently drop every previously-synced objective not included in this particular
   * response. Completed-objective removal from this cache is not this method's concern — the
   * fetch never returns a completed row in the first place, by design.
   */
  aggregateNextCallObjectives(nextCallObjectiveData, isDelta = false) {
    // De-dupe within this response first (same safety the original code had) before deciding
    // whether to merge or replace.
    const incoming: NextCallObjective[] = [];
    nextCallObjectiveData.forEach(a => {
      if (a.indskr_callobjectiveid && !incoming.some(o => o.objectiveID === a.indskr_callobjectiveid)) {
        incoming.push(new NextCallObjective(a));
      }
    });

    if (!isDelta) {
      this.nextCallObjectiveService.nextCallObjectives = incoming;
      return;
    }

    const merged = new Map(this.nextCallObjectiveService.nextCallObjectives.map(nco => [nco.objectiveID, nco]));
    incoming.forEach(nco => {
      if (nco.stateCode === NextCallObjectiveStateCode.active) {
        merged.set(nco.objectiveID, nco);
      } else {
        merged.delete(nco.objectiveID);
      }
    });
    this.nextCallObjectiveService.nextCallObjectives = Array.from(merged.values());
  }

  /**
   * @description This method is called to update offline nco changes
  */
  async uploadOfflineNextCallObjectives() {
    if(this.device.isOffline) return;
    await this.getDataFromIndexDB();
    let pendingList = this.nextCallObjectiveService.nextCallObjectives.filter(nco => {
      return nco.syncPending;
    });
    if(pendingList && pendingList.length > 0) {
      this.bulkNextCallService(pendingList);
    }
  }
  /**
   * @description This method bulk service which is called for create/update/delete nco
   * @param {nextCallObjective[]=} nextCallObjectiveList nco list for the service call
  */
  async bulkNextCallService(nextCallObjectiveList) {
    let data: any[] = [];
    let syncableObjectives: NextCallObjective[] = [];
    nextCallObjectiveList.forEach(element => {
      this.updateObjectiveToServiceList(element);
      // Objectives captured/completed against a meeting that is still offline (not yet in Dynamics)
      // must ride along with the meeting upload payload (see uploadDataForNextCallObjectives). Posting
      // them standalone here would send an unresolvable offline meeting id as indskr_MeetingCaptured,
      // orphaning or dropping the objective while syncPending is cleared, so the data is lost on delta.
      // Leave them syncPending = true so they are uploaded with the meeting instead.
      if (this.isCapturedOnOfflineMeeting(element)) {
        return;
      }
      syncableObjectives.push(element);
      data.push(this.getServiceDTO(element));
    });
    if (data.length === 0) return;
    if(!this.device.isOffline){
      let url = this.authService.userConfig.activeInstance.entryPointUrl + Endpoints.nextCallObjective.OFFLINE_NEXT_CALL_OBJECTIVE;
      try {
        const response = await this.http.post<any[]>(url, data).toPromise();
        if (response) {
          response.forEach((element) => {
            if (!element.errorMessage && !element.errorId) {
              let ncoIndex = findIndex(syncableObjectives, { externalid: element.indskr_externalid });
              if (ncoIndex != -1) {
                syncableObjectives[ncoIndex].objectiveID = element['indskr_callobjectiveid'];
                syncableObjectives[ncoIndex].syncPending = false;
                this.updateObjectiveToServiceList(syncableObjectives[ncoIndex]);
              }
            } else {
              // Leave syncPending = true so this objective is retried on the next offline upload / delta.
              console.error('bulkNextCallService: objective sync returned error', element.errorMessage || element.errorId);
            }
          });
        }
        return response;
      } catch (error) {
        // Leave syncPending = true so the objectives are retried on the next offline upload / delta
        // instead of being silently dropped.
        console.error('bulkNextCallService: failed to sync next call objectives', error);
      }
    }
  }

  /**
   * @description Returns true when the objective is captured or completed against a meeting that has
   * not yet synced to Dynamics (still holds a temporary offline id). Such objectives cannot be linked
   * via the standalone call-objective endpoint and must be uploaded together with the meeting.
   * @param {NextCallObjective=} objective the objective to inspect
   */
  private isCapturedOnOfflineMeeting(objective): boolean {
    return Boolean(
      (objective.capturedMeetingId && objective.capturedMeetingId.includes(OFFLINE_ID_PREFIX))
      || (objective.completedMeetingId && objective.completedMeetingId.includes(OFFLINE_ID_PREFIX))
    );
  }

  /**
   * @description This method create nco service data for service call for create/update/delete nco
   * @param {NextCallObjective=} objective nco to convert it to service dto
   * @returns {NextCallObjectiveServiceDTO=} serive nco dto for service call
  */
  public getServiceDTO(objective : NextCallObjective) : NextCallObjectiveServiceDTO{
    if(!objective.objectiveID || objective.objectiveID == '') {
      let obj = find(this.nextCallObjectiveService.nextCallObjectives, {externalid : objective.externalid});
      if(obj) {
        objective.objectiveID = obj.objectiveID;
      }
    }
    return {
      indskr_callobjectiveid : objective.objectiveID,
      indskr_callobjectives : objective.objective,
      statuscode : objective.statusCode,
      statecode : objective.stateCode,
      indskr_externalid : objective.externalid,
      indskr_MeetingCaptured: objective.capturedMeetingId,
      indskr_MeetingCompleted: objective.completedMeetingId,
      indskr_User: this.authService.user.systemUserID,
      indskr_Customer_contact: objective.contactId,
      deletestate: objective.deletestate ? 1 : 0,
      createdon: objective.createdOn,
    }
  }

  /**
   * @description This method updates nco data to the index db from the local variable of the nco list
  */
  private updateIndexDB() {
    this.disk.updateOrInsert(DB_KEY_PREFIXES.NEXT_CALL_OBJECTIVES, (doc) => {
      doc = {
        raw: []
      };
      doc.raw =[...this.nextCallObjectiveService.nextCallObjectives];
      doc.lastModified = new Date().getTime();
      return doc;
    });
  }

  /**
   * @description This method get nco data from the index db and stores it in the local variable of the nco list
  */
  private async getDataFromIndexDB() {
    await this.disk.retrieve(DB_KEY_PREFIXES.NEXT_CALL_OBJECTIVES, true).then((doc) => {
      if (doc && doc.raw) {
        this.nextCallObjectiveService.nextCallObjectives = doc.raw;
      }
    })
  }

  /**
   * @description This method update the index db and the local variable of the nco list with the ncoId
   *  if the meeting is created offline
   * @param {any=} objective service response from bulk nco service
  */
  /**
   * @description Re-points objectives at a meeting that has just been given its real Dynamics id,
   * and uploads the ones that were waiting on it. An objective captured in the same breath as a new
   * meeting copies whatever id the meeting held at that moment; when the meeting's create resolves
   * late its id is replaced, leaving the objective pointing at an id that no longer exists — held
   * back from sync by isCapturedOnOfflineMeeting and absent from the meeting's own objectives,
   * which read by the new id.
   * @param {string=} previousMeetingId the temporary id the objectives were captured against
   * @param {string=} meetingId the meeting's real id
   */
  public async remapObjectivesToMeeting(previousMeetingId: string, meetingId: string): Promise<void> {
    if (!previousMeetingId || !meetingId || previousMeetingId === meetingId) {
      return;
    }
    const remapped: NextCallObjective[] = [];
    for (const objective of this.nextCallObjectiveService.nextCallObjectives ?? []) {
      let touched = false;
      if (objective.capturedMeetingId === previousMeetingId) {
        objective.capturedMeetingId = meetingId;
        touched = true;
      }
      if (objective.completedMeetingId === previousMeetingId) {
        objective.completedMeetingId = meetingId;
        touched = true;
      }
      if (touched && objective.syncPending) {
        remapped.push(objective);
      }
    }
    if (!remapped.length) {
      return;
    }
    try {
      this.updateIndexDB();
    } catch (error) {
      console.error('remapObjectivesToMeeting: storing the re-pointed objectives failed', error);
    }
    // They were only pending because the meeting had no real id; now it has one they can go.
    await this.bulkNextCallService(remapped);
  }

  private updateObjectiveToServiceList(objective) {
    let index = findIndex(this.nextCallObjectiveService.nextCallObjectives, {externalid : objective.externalid});
    if(index != -1) {
      this.nextCallObjectiveService.nextCallObjectives[index] = objective;
    }
    else {
      let index_objective_id = findIndex(this.nextCallObjectiveService.nextCallObjectives, {objectiveID : objective.objectiveID});
      if(index_objective_id != -1 && objective.objectiveID && objective.objectiveID != '') {
        this.nextCallObjectiveService.nextCallObjectives[index] = objective;
      }
      else {
        this.nextCallObjectiveService.nextCallObjectives.push(objective);
      }
    }
    try {
      this.updateIndexDB();
    }
    catch (error) {
      console.log(error);
    }
    this.trackOfflineNextCallObjectiveDataCount();
  }

  async loadOfflineNextCallObjective() {
    await this.getDataFromIndexDB();
    let pendingList = this.nextCallObjectiveService.nextCallObjectives.filter(nco => {
      return nco.syncPending && (nco.capturedMeetingId.includes('offline_meeting') || nco.completedMeetingId.includes('offline_meeting'));
    });
    return pendingList;
  }

  updateNCOForOfflineMeetings(offlineMeetings) {
    // Loop through offlineMeetings response
    for (const key in offlineMeetings) {
      if (offlineMeetings.hasOwnProperty(key)) {
        const element = offlineMeetings[key];
        if(element.offlineNextCallObjectives) {
          element.offlineNextCallObjectives.forEach(nco => {
            let ncoIndex =  findIndex(this.nextCallObjectiveService.nextCallObjectives, { externalid : nco.indskr_externalid});
            if(ncoIndex != -1 && nco.indskr_callobjectiveid && nco.indskr_callobjectiveid != '') {
              this.nextCallObjectiveService.nextCallObjectives[ncoIndex].objectiveID = nco.indskr_callobjectiveid;
              this.nextCallObjectiveService.nextCallObjectives[ncoIndex].syncPending = false;
              if(nco['indskr_objectivecaptured@odata.bind']) {
                this.nextCallObjectiveService.nextCallObjectives[ncoIndex].capturedMeetingId = element.activityId;
              }
              if(nco['indskr_objectivecompleted@odata.bind']) {
                this.nextCallObjectiveService.nextCallObjectives[ncoIndex].completedMeetingId = element.activityId;
              }
            }
          });
        }
      }
    }
    try {
      this.updateIndexDB();
    }
    catch (error) {
      console.log(error);
    }
    this.trackOfflineNextCallObjectiveDataCount();
  }

  trackOfflineNextCallObjectiveDataCount() {
    const offlineDataCount: number = this.nextCallObjectiveService.nextCallObjectives.filter(s => s.syncPending === true).length;
    this.disk.setOfflineDataCount(OFFLINE_DATA_COUNT_ENTITY_NAME.NEXT_CALL_OBJECTIVES, offlineDataCount);
  }

}
